import { useState } from 'react'
import { Loader2, RotateCcw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchPosPaymentMethods } from '@/lib/api/paymentMethods'
import { fetchSale, refundSale } from '@/lib/api/refunds'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { formatLedgerMoney } from '@/lib/api/accounts'
import { cn } from '@/lib/utils'
import { useEffect } from 'react'

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function RefundsPanel() {
  const [saleId, setSaleId] = useState('')
  const [sale, setSale] = useState(null)
  const [methods, setMethods] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({
    reason: '',
    manager_email: '',
    manager_password: '',
    payment_method_id: '',
  })

  useEffect(() => {
    fetchPosPaymentMethods()
      .then((list) => setMethods(list ?? []))
      .catch(() => setMethods([]))
  }, [])

  async function handleLookup(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setSuccess('')
    setSale(null)
    try {
      const found = await fetchSale(Number(saleId))
      setSale(found)
      const cash = (methods ?? []).find((m) => m.code === 'cash')
      setForm((p) => ({ ...p, payment_method_id: String(cash?.id ?? methods[0]?.id ?? '') }))
    } catch (err) {
      setError(apiErrorMessage(err, 'Sale not found.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleRefund(e) {
    e.preventDefault()
    if (!sale) return
    setBusy(true)
    setError('')
    setSuccess('')
    try {
      const amount = Number(sale.total ?? sale.grand_total ?? 0)
      await refundSale(sale.id, {
        reason: form.reason,
        manager_email: form.manager_email,
        manager_password: form.manager_password,
        refunds: [
          {
            payment_method_id: Number(form.payment_method_id),
            amount,
          },
        ],
      })
      setSuccess(`Sale #${sale.id} refunded.`)
      setSale(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'Refund failed.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-2">
        <RotateCcw className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Refunds</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Look up a completed ticket by ID, then refund with manager approval. Cash refunds update the day&apos;s expected drawer cash.
      </p>

      <form onSubmit={handleLookup} className="flex flex-wrap gap-2">
        <input
          className={cn(inputClass, 'max-w-[12rem]')}
          placeholder="Sale ID"
          value={saleId}
          onChange={(e) => setSaleId(e.target.value)}
          required
        />
        <Button type="submit" disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Look up
        </Button>
      </form>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-700">{success}</p> : null}

      {sale ? (
        <form onSubmit={handleRefund} className="grid max-w-lg gap-3 rounded-2xl border border-border/70 bg-card p-4">
          <p className="text-sm">
            <span className="font-medium">Sale #{sale.id}</span>
            {' · '}
            AED {formatLedgerMoney(sale.total ?? sale.grand_total)}
            {sale.customer?.name ? ` · ${sale.customer.name}` : ''}
            {' · '}
            {sale.status}
          </p>
          <input
            className={inputClass}
            placeholder="Reason"
            required
            value={form.reason}
            onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
          />
          <input
            className={inputClass}
            type="email"
            placeholder="Manager email"
            required
            value={form.manager_email}
            onChange={(e) => setForm((p) => ({ ...p, manager_email: e.target.value }))}
          />
          <input
            className={inputClass}
            type="password"
            placeholder="Manager password"
            required
            value={form.manager_password}
            onChange={(e) => setForm((p) => ({ ...p, manager_password: e.target.value }))}
          />
          <select
            className={inputClass}
            required
            value={form.payment_method_id}
            onChange={(e) => setForm((p) => ({ ...p, payment_method_id: e.target.value }))}
          >
            <option value="">Refund method</option>
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name ?? m.code}
              </option>
            ))}
          </select>
          <Button type="submit" variant="destructive" disabled={busy || sale.status === 'refunded'}>
            Refund full amount
          </Button>
        </form>
      ) : null}
    </div>
  )
}
